import { PopoverCSSVars } from "../../../core/ui/popover/vars.js";
import { getPositioningBoundaryRect, resolvePositioningBoundary } from "../../utils/layout.js";
import { isEventWithinElement } from "../../utils/event.js";
import { getAnchorPositionStyle, getFixedContainingBlockOrigin, getPopupPositionRect, offsetRect, resolveOffsets } from "./positioning.js";
import { applyStyles, getAnchorNames, getPositionedSide, isRTL, observeResize, rafThrottle, restoreInlineStyles, snapshotInlineStyles, supportsAnchorPositioning } from "@videojs/utils/dom";
import { kebabCase } from "@videojs/utils/string";
//#region src/dom/ui/popover/positioner.ts
const POPUP_STYLE_PROPS = [
	"position",
	"inset",
	"margin",
	"margin-top",
	"margin-right",
	"margin-bottom",
	"margin-left",
	"justify-self",
	"align-self",
	"margin-inline-start",
	"margin-block-start",
	"translate",
	"top",
	"right",
	"bottom",
	"left"
];
/**
* Positions a popup and tracks layout changes while it is active.
*
* @internal
*/
var PopupPositioner = class {
	#options = null;
	#boundaryElement = null;
	#abort = null;
	#stopObservingResize = null;
	#triggerAnchorName = null;
	#triggerAnchorAdded = false;
	#popupAnchor = null;
	#popupStyles = null;
	#reposition = rafThrottle(() => this.#position());
	sync(options) {
		const { anchorName, position, trigger, popup, boundary, container, cssVars = PopoverCSSVars } = options;
		if (!position || !trigger || !popup) {
			this.cleanup();
			return;
		}
		const boundaryElement = resolvePositioningBoundary(boundary, {
			container: container ?? null,
			root: popup.getRootNode()
		});
		const previous = this.#options;
		if (!previous || previous.anchorName !== anchorName || previous.trigger !== trigger || previous.popup !== popup || (previous.cssVars ?? PopoverCSSVars) !== cssVars || previous.trackResize !== options.trackResize || this.#boundaryElement !== boundaryElement) {
			if (previous?.popup) this.#restorePopupStyles(previous.popup);
			this.#stopTracking();
			this.#options = {
				...options,
				cssVars
			};
			this.#boundaryElement = boundaryElement;
			this.#startTracking();
		} else this.#options = {
			...options,
			cssVars
		};
		this.#position();
	}
	cleanup() {
		if (!this.#options) return;
		if (this.#options.popup) this.#restorePopupStyles(this.#options.popup);
		this.#stopTracking();
		this.#options = null;
		this.#boundaryElement = null;
	}
	#startTracking() {
		const options = this.#options;
		if (!options?.trigger || !options.popup) return;
		this.#applyAnchorStyles(options.trigger, options.popup, options.anchorName);
		this.#abort = new AbortController();
		const { signal } = this.#abort;
		window.addEventListener("scroll", this.#schedule, {
			capture: true,
			passive: true,
			signal
		});
		window.addEventListener("resize", this.#schedule, { signal });
		const resizeTargets = [options.trigger];
		if (options.trackResize !== false) resizeTargets.push(options.popup);
		if (this.#boundaryElement) resizeTargets.push(this.#boundaryElement);
		this.#stopObservingResize = observeResize(resizeTargets, () => this.#schedule());
	}
	#stopTracking() {
		this.#abort?.abort();
		this.#abort = null;
		this.#stopObservingResize?.();
		this.#stopObservingResize = null;
		this.#reposition.cancel();
		this.#restoreAnchorStyles();
	}
	#schedule = (event) => {
		const popup = this.#options?.popup;
		if (!popup || event && isEventWithinElement(event, popup)) return;
		this.#reposition();
	};
	#position() {
		const options = this.#options;
		if (!options?.position || !options.trigger || !options.popup) return;
		const trigger = options.trigger;
		const anchorSupported = supportsAnchorPositioning();
		const origin = anchorSupported ? {
			x: 0,
			y: 0
		} : getFixedContainingBlockOrigin(options.popup);
		const triggerRect = offsetRect(trigger.getBoundingClientRect(), origin);
		const boundaryRect = offsetRect(getPositioningBoundaryRect(this.#boundaryElement), origin);
		const offsets = resolveOffsets(options.popup, options.cssVars);
		const preferredPosition = options.position;
		const measure = () => offsetRect(getPopupPositionRect(options.popup, preferredPosition.side), origin);
		const getPosition = (popupRect) => {
			const side = getPositionedSide(triggerRect, popupRect, boundaryRect, preferredPosition, offsets);
			const { positionAnchor: _, ...style } = getAnchorPositionStyle(options.anchorName, {
				...preferredPosition,
				side,
				direction: isRTL(trigger) ? "rtl" : "ltr"
			}, triggerRect, anchorSupported ? void 0 : popupRect, boundaryRect, offsets, options.cssVars);
			return {
				popupRect,
				side,
				style
			};
		};
		const position = getPosition(measure());
		this.#capturePopupStyles(options.popup, options.cssVars ?? PopoverCSSVars);
		applyStyles(options.popup, position.style);
		options.onSideChange?.(position.side);
		if (anchorSupported || !options.onSideChange) return;
		const popupRect = measure();
		if (popupRect.width === position.popupRect.width && popupRect.height === position.popupRect.height) return;
		const nextPosition = getPosition(popupRect);
		applyStyles(options.popup, nextPosition.style);
		if (nextPosition.side !== position.side) options.onSideChange(nextPosition.side);
	}
	#capturePopupStyles(popup, cssVars) {
		if (this.#popupStyles) return;
		const props = [
			...POPUP_STYLE_PROPS,
			cssVars.anchorWidth,
			cssVars.anchorHeight,
			cssVars.availableWidth,
			cssVars.availableHeight
		];
		this.#popupStyles = snapshotInlineStyles(popup, props);
	}
	#restorePopupStyles(popup) {
		if (!this.#popupStyles) return;
		restoreInlineStyles(popup, this.#popupStyles);
		this.#popupStyles = null;
	}
	#applyAnchorStyles(trigger, popup, anchorName) {
		if (!supportsAnchorPositioning()) return;
		const generatedName = `--${anchorName}`;
		const triggerAnchor = this.#readStyle(trigger, "anchor-name");
		this.#popupAnchor = this.#readStyle(popup, "position-anchor");
		const names = getAnchorNames(trigger);
		this.#triggerAnchorName = generatedName;
		this.#triggerAnchorAdded = !names.includes(generatedName);
		if (this.#triggerAnchorAdded) names.push(generatedName);
		trigger.style.setProperty("anchor-name", names.join(", "), triggerAnchor.priority);
		popup.style.setProperty("position-anchor", generatedName);
	}
	#restoreAnchorStyles() {
		const options = this.#options;
		if (!options?.trigger || !options.popup) return;
		if (this.#triggerAnchorName && this.#triggerAnchorAdded) {
			const current = this.#readStyle(options.trigger, "anchor-name");
			const names = getAnchorNames(options.trigger).filter((name) => name !== this.#triggerAnchorName);
			this.#writeStyle(options.trigger, "anchor-name", {
				value: names.join(", "),
				priority: current.priority
			});
		}
		if (this.#popupAnchor) this.#writeStyle(options.popup, "position-anchor", this.#popupAnchor);
		this.#triggerAnchorName = null;
		this.#triggerAnchorAdded = false;
		this.#popupAnchor = null;
	}
	#readStyle(element, prop) {
		const name = prop.startsWith("--") ? prop : kebabCase(prop);
		return {
			value: element.style.getPropertyValue(name),
			priority: element.style.getPropertyPriority(name)
		};
	}
	#writeStyle(element, prop, style) {
		const name = prop.startsWith("--") ? prop : kebabCase(prop);
		if (style.value) element.style.setProperty(name, style.value, style.priority);
		else element.style.removeProperty(name);
	}
};
//#endregion
export { PopupPositioner };
